import { useState, useEffect, FormEvent } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useAuthStore } from '@/stores/authStore';
import './LoginPage.css';

export function LoginPage() {
  const [email, setEmail] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const { login, isAuthenticated } = useAuthStore();
  const navigate = useNavigate();
  const location = useLocation();

  const from = (location.state as { from?: Location })?.from?.pathname || '/dashboard';

  // Redirect if already authenticated
  useEffect(() => {
    if (isAuthenticated) {
      navigate(from, { replace: true });
    }
  }, [isAuthenticated, from, navigate]);

  const validateEmail = (email: string): boolean => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setError(null);

    if (!validateEmail(email)) {
      setError('Please enter a valid email address.');
      return;
    }

    setIsSubmitting(true);

    try {
      await login(email);
      // Store email in sessionStorage for OTP page
      sessionStorage.setItem('pendingAuthEmail', email);
      setIsSuccess(true);
    } catch {
      setError('Failed to send login email. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="login-page">
      {/* Decorative corners */}
      <div className="corner corner-tl" />
      <div className="corner corner-tr" />
      <div className="corner corner-bl" />
      <div className="corner corner-br" />

      <div className="login-container">
        {/* Brand */}
        <div className="brand">
          <Link to="/" className="logo-link">
            <h1 className="logo">
              Page<span>Craft</span>
            </h1>
            <p className="studio">Studio</p>
          </Link>
        </div>

        {!isSuccess ? (
          <>
            {/* Login form */}
            <h2 className="login-title">Welcome Back</h2>
            <p className="login-subtitle">
              Enter your email to receive a magic link or verification code
            </p>

            <form className="login-form" onSubmit={handleSubmit}>
              <div className="form-field">
                <label htmlFor="email" className="form-label">
                  Email Address
                </label>
                <input
                  id="email"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="you@example.com"
                  className="email-input"
                  disabled={isSubmitting}
                  autoComplete="email"
                  autoFocus
                  required
                />
              </div>

              {error && (
                <div className="form-message error" role="alert">
                  {error}
                </div>
              )}

              <button
                type="submit"
                className="submit-btn"
                disabled={isSubmitting || !email}
              >
                {isSubmitting ? 'Sending...' : 'Continue with Email'}
              </button>
            </form>

            <p className="signup-link">
              Don't have an account?{' '}
              <span className="muted">Signing in will create one</span>
            </p>
          </>
        ) : (
          <>
            {/* Success state */}
            <div className="success-container">
              <div className="success-icon">
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="48"
                  height="48"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <rect x="2" y="4" width="20" height="16" rx="2" />
                  <path d="m22 7-8.97 5.7a1.94 1.94 0 0 1-2.06 0L2 7" />
                </svg>
              </div>
              <h2 className="success-title">Check Your Email</h2>
              <p className="success-message">
                We've sent a verification link and code to{' '}
                <strong>{email}</strong>
              </p>
              <p className="success-instructions">
                Click the link in the email or enter the 6-digit code below.
              </p>

              <Link to="/auth/verify" className="verify-link-btn">
                Enter Verification Code
              </Link>

              <button
                type="button"
                className="resend-btn"
                onClick={() => {
                  setIsSuccess(false);
                  setEmail('');
                }}
              >
                Use a different email
              </button>
            </div>
          </>
        )}

        <footer className="login-footer">
          <Link to="/" className="back-link">
            &larr; Back to Home
          </Link>
        </footer>
      </div>
    </div>
  );
}
